import { useState, useEffect } from "react";
import { ModalNew as Modal } from "components/Modal/ModalNew";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { ProjectForm } from "components/ProjectForm/ProjectForm";
import { addPastProject, getTradeValues } from "actions/companyActions";
import { showToast } from "utils/helpers";
import { useCurrentCompany } from "hooks/auth";

interface AddNewProjectModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreated: (id: number) => void;
}

export const AddNewProjectModal: React.FC<AddNewProjectModalProps> = ({
	isOpen,
	onClose,
	onCreated,
}) => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const activeCompany = useCurrentCompany() as {
		id?: number;
		profile_type?: { id?: number };
	};
	const profileTypeId = activeCompany?.profile_type?.id;
	const [errorStatus, setErrorStatus] = useState(false);

	useEffect(() => {
		if (isOpen) {
			(dispatch as { (dispatch): Promise<void> })(getTradeValues());
		}
	}, [dispatch, isOpen]);

	const initialValues = {
		company_id: activeCompany?.id ?? "",
		name: "",
		location: "",
		summary: "",
		value: profileTypeId === 3 ? "0" : "",
		year_completed: "",
		funding_type: "",
		site_area: "",
		gross_floor_area: "",
		client_name: "",
		head_contractor: "",
		sector_id: "",
		scope: [],
		images: [],
		innovation_value_summary: "",
		awards: "",
		testimonials: "",
		status: "",
		procurement_method_ids: [],
		trade_ids: [],
		trade_value: "",
		disciplines: [{ expertise: "", disciplines: [] }],
	};

	const handleSubmit = async (
		values: Record<string, unknown>,
		{ setErrors }: { setErrors: (errors: Record<string, string>) => void }
	) => {
		try {
			const result = await (dispatch as { (values): { id; data, } })(addPastProject(values));
			const newId = result?.data?.id ?? result?.id;
			queryClient.invalidateQueries({ queryKey: ["projectExperience"] });
			if (newId) {
				onCreated(newId);
			}
			onClose();
			showToast("Project added successfully.", "Success", true);
		} catch (e: unknown) {
			if (e && typeof e === "object" && "response" in e) {
				const error = await (
					e as { response: { json: () => Promise<{ errors: Record<string, string>; message: string }> } }
				).response.json();
				setErrors(error.errors);
				setErrorStatus(true);
				showToast(error.message, "Error");
			} else {
				showToast("An unexpected error occurred.", "Error");
			}
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			headerContent="Add New Project"
		>
			<ProjectForm
				// @ts-ignore
				initialValues={initialValues}
				handleSubmit={handleSubmit}
				profileTypeId={profileTypeId}
				errorStatus={errorStatus}
				setErrorStatus={setErrorStatus}
			/>
		</Modal>
	);
};
